import { CircleHelp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CountryLabel } from "@/components/CountryLabel";
import { CountrySafetySignal } from "@/components/CountrySafetySignal";
import { ComparisonMetricLabel } from "@/components/compare/ComparisonMetricLabel";
import { comparisonMetrics } from "@/components/compare/comparisonConfig";
import { getCountryTrustSignal } from "@/lib/countryTrust";
import { truncateReasonText } from "@/lib/reasoningQuality";
import { cn } from "@/lib/utils";

const toneClasses = {
  strong: "bg-[#22C55E]",
  moderate: "bg-[#F59E0B]",
  situational: "bg-[#7C3AED]",
};

const getTone = (score, scores) => {
  if (!scores.length) return "situational";
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);
  if (maxScore === minScore) return "strong";
  if (score === maxScore) return "strong";
  if (score === minScore) return "situational";
  return "moderate";
};

const getComparisonRole = (country, index) => {
  if (country?.role_label === "Current lead") {
    return {
      label: "Current lead",
      className: "border-amber-200 bg-amber-100 text-amber-900",
    };
  }

  if (country?.role_label === "Closest alternative") {
    return {
      label: "Closest alternative",
      className: "border-stone-200 bg-stone-100 text-stone-700",
    };
  }

  if (index === 0) {
    return {
      label: "Current lead",
      className: "border-amber-200 bg-amber-100 text-amber-900",
    };
  }

  if (index === 1) {
    return {
      label: "Closest alternative",
      className: "border-stone-200 bg-stone-100 text-stone-700",
    };
  }

  return null;
};

const ScoreBars = ({ score, tone, dataTestId }) => {
  const filledBars = Math.max(1, Math.round(score / 20));

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={dataTestId}>
      {Array.from({ length: 5 }).map((_, index) => (
        <span
          key={`${dataTestId}-${index}`}
          className={cn(
            "h-3 w-4 rounded-sm border border-black/5",
            index < filledBars ? toneClasses[tone] : "bg-slate-100"
          )}
        />
      ))}
    </div>
  );
};

export const CountryComparisonMatrix = ({ countries, onRemoveCountry, reasoningMap = {} }) => {
  if (!countries.length) {
    return null;
  }

  const bestOverallScore = Math.max(...countries.map((country) => country.overall_score));
  const bestOverallCode = countries.find((country) => country.overall_score === bestOverallScore)?.code;
  const leadCountryCode = countries[0]?.code;
  const leadAlsoHasBestScore = leadCountryCode === bestOverallCode;
  const canRemove = countries.length > 1;
  const labelColumnWidth = countries.length >= 4 ? 240 : countries.length === 3 ? 260 : 280;
  const tableMinWidth = countries.length >= 4 ? 1160 : countries.length === 3 ? 980 : 820;
  const countryColumnWidth = `calc((100% - ${labelColumnWidth}px) / ${countries.length})`;

  return (
    <div className="overflow-x-auto rounded-[2rem] border border-stone-200/80 bg-[rgba(255,255,255,0.86)] shadow-[0_28px_70px_rgba(28,25,23,0.08)] backdrop-blur-sm" data-testid="comparison-matrix-wrapper">
      <table className="w-full table-fixed border-separate border-spacing-0" style={{ minWidth: `${tableMinWidth}px` }} data-testid="comparison-matrix-table">
        <colgroup>
          <col style={{ width: `${labelColumnWidth}px` }} />
          {countries.map((country) => (
            <col key={`col-${country.code}`} style={{ width: countryColumnWidth }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="border-b border-stone-200/80 bg-stone-50/85 p-5 text-left align-bottom">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Country Comparison</p>
            </th>
            {countries.map((country, index) => {
              const isBestOverall = country.code === bestOverallCode;
              const trustSignal = getCountryTrustSignal(country);
              const comparisonRole = getComparisonRole(country, index);
              return (
                <th
                  key={country.code}
                  className={cn(
                    "border-b border-l border-stone-200/80 p-5 align-top bg-white/80",
                    isBestOverall && "bg-amber-50/70"
                  )}
                  data-testid={`comparison-column-${country.code}`}
                >
                  <div className="relative">
                    <div className="mx-auto flex max-w-[220px] flex-col items-center text-center">
                      <div className="min-w-0">
                        <div className="flex items-start justify-center gap-1.5">
                        <CountryLabel
                          code={country.code}
                          name={country.name}
                          size={18}
                          className="justify-center"
                          textClassName="text-center font-manrope text-lg font-semibold text-foreground"
                          dataTestId={`comparison-column-label-${country.code}`}
                          flagTestId={`comparison-column-flag-${country.code}`}
                        />
                          {canRemove && country?.code ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => onRemoveCountry(country.code)}
                                    className="mt-0.5 h-7 w-7 rounded-full text-stone-500 hover:bg-stone-100"
                                    data-testid={`remove-country-${country.code}`}
                                    aria-label={`Remove ${country.name} from comparison`}
                                    data-country-code={country.code}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Remove from comparison</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null}
                        </div>
                        <p
                          className="mt-2 text-xs text-stone-500"
                          title={trustSignal.label}
                          data-testid={`comparison-review-signal-${country.code}`}
                        >
                          {trustSignal.label}
                        </p>
                        <CountrySafetySignal
                          safetyProfile={country.safety_profile}
                          dataTestIdPrefix={`comparison-safety-signal-${country.code}`}
                        />
                        {comparisonRole ? (
                          <div
                            className={cn(
                              "mt-2 inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium",
                              comparisonRole.className
                            )}
                            data-testid={`comparison-role-pill-${country.code}`}
                          >
                            {comparisonRole.label}
                          </div>
                        ) : null}
                        {trustSignal.warning ? (
                          <p
                            className="mt-1 text-[11px] leading-relaxed text-stone-500"
                            data-testid={`comparison-review-warning-${country.code}`}
                          >
                            {trustSignal.warning}
                          </p>
                        ) : null}
                      </div>
                      {isBestOverall && (
                        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900" data-testid={`best-fit-pill-${country.code}`}>
                          <span>{leadAlsoHasBestScore ? "🏆 Strongest overall fit in this set" : "🏆 Highest score in this set"}</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center text-amber-800/75 transition-colors hover:text-amber-900"
                                  data-testid={`best-fit-pill-tooltip-trigger-${country.code}`}
                                  aria-label={`More information about Best Overall Fit for ${country.name}`}
                                >
                                  <CircleHelp className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-[240px] text-xs text-white">
                                  {leadAlsoHasBestScore
                                    ? "This country looks strongest overall in this set once your priorities and assessment answers are weighed together."
                                    : "This country posts the highest matrix score, even if another option still looks stronger in the broader decision."}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr data-testid="overall-fit-score-row">
            <td className="bg-stone-50/80 p-5 align-top">
              <ComparisonMetricLabel
                label="Overall Fit Score"
                description="Use this as a quick summary of how each country performs across the factors you care about. Let it support your judgment, not replace it."
                dataTestId="overall-fit-score-label"
              />
            </td>
            {countries.map((country) => {
              const isBestOverall = country.code === bestOverallCode;
              const fitReason = truncateReasonText(country.plan_role_summary || country.role_summary || reasoningMap[country.code]?.fitReasons?.[0] || "");
              const tradeoffReason = truncateReasonText(reasoningMap[country.code]?.tradeoffReasons?.[0] || "");
              return (
                <td
                  key={`${country.code}-overall`}
                  className={cn("border-l border-stone-200/80 p-5 align-top bg-white/80", isBestOverall && "bg-amber-50/60")}
                  data-testid={`overall-fit-cell-${country.code}`}
                >
                  <div className="flex flex-col items-center justify-center space-y-3 text-center">
                    <p className="text-4xl font-manrope font-bold text-stone-900" data-testid={`overall-fit-score-${country.code}`}>
                      {country.overall_score}
                    </p>
                    <ScoreBars
                      score={country.overall_score}
                      tone={isBestOverall ? "strong" : getTone(country.overall_score, countries.map((item) => item.overall_score))}
                      dataTestId={`overall-fit-bars-${country.code}`}
                    />
                    {(fitReason || tradeoffReason) ? (
                      <div className="w-full max-w-[260px] space-y-2 text-left" data-testid={`comparison-rationale-${country.code}`}>
                        {fitReason ? (
                          <p className="text-xs leading-5 text-stone-600" data-testid={`comparison-fit-rationale-${country.code}`}>
                            <span className="font-semibold text-stone-900">Why it looks strong:</span> {fitReason}
                          </p>
                        ) : null}
                        {tradeoffReason ? (
                          <p className="text-xs leading-5 text-stone-600" data-testid={`comparison-tradeoff-rationale-${country.code}`}>
                            <span className="font-semibold text-stone-900">What to watch:</span> {tradeoffReason}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </td>
              );
            })}
          </tr>
          {comparisonMetrics.map((metric) => {
            const metricScores = countries.map((country) => country.metrics[metric.id]);
            const isHealthcareMetric = metric.id === "healthcare_quality";
            return (
              <tr key={metric.id} data-testid={`metric-row-${metric.id}`}>
                <td className="border-t border-stone-200/80 bg-stone-50/80 p-5 align-top">
                  <ComparisonMetricLabel
                    label={metric.label}
                    description={metric.description}
                    dataTestId={`metric-label-${metric.id}`}
                  />
                </td>
                {countries.map((country) => {
                  const score = country.metrics[metric.id];
                  const tone = getTone(score, metricScores);
                  const isBestOverall = country.code === bestOverallCode;
                  const healthcareLabel = country.healthcare_confidence_label || "Moderate Confidence";
                  const healthcareSentence = country.healthcare_us_relative || "";
                  return (
                    <td
                      key={`${country.code}-${metric.id}`}
                      className={cn("border-t border-l border-stone-200/80 p-5 align-top bg-white/80", isBestOverall && "bg-amber-50/40")}
                      data-testid={`metric-cell-${metric.id}-${country.code}`}
                    >
                      {isHealthcareMetric ? (
                        <div className="mx-auto flex max-w-[240px] flex-col items-center justify-center text-center" data-testid={`healthcare-confidence-cell-${country.code}`}>
                          <div className="flex items-center justify-center gap-2">
                            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-semibold text-stone-900" data-testid={`healthcare-confidence-label-${country.code}`}>
                              {healthcareLabel}
                            </span>
                            {healthcareSentence ? (
                              <div className="hidden md:block">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex items-center text-stone-400 transition-colors hover:text-stone-900"
                                        data-testid={`healthcare-confidence-tooltip-trigger-${country.code}`}
                                        aria-label={`Healthcare context for ${country.name}`}
                                      >
                                        <CircleHelp className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-[240px] text-xs text-white">{healthcareSentence}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            ) : null}
                          </div>
                          {healthcareSentence ? (
                            <p className="mt-2 text-xs leading-5 text-stone-500 md:hidden" data-testid={`healthcare-confidence-inline-${country.code}`}>
                              {healthcareSentence}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-3">
                          <span className="inline-flex w-12 shrink-0 justify-center text-center text-2xl font-manrope font-semibold text-stone-900" data-testid={`metric-score-${metric.id}-${country.code}`}>
                            {score}
                          </span>
                          <ScoreBars score={score} tone={tone} dataTestId={`metric-bars-${metric.id}-${country.code}`} />
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};